import type { WorkToastItem } from "@/stores/work-toast-store";
import { workToastExecutionId } from "./computer-toast-key";

/** Evidence of an app/computer tool call, independent of toast UI state. */
export function hasComputerUseEvidence(item: WorkToastItem | undefined): boolean {
  return Boolean(
    item &&
      (item.computer ||
        item.apps?.some((app) => app.confirmed && (app.route === "ax" || app.route === "mcp")) ||
        (item.source === "computer" && item.activity?.some((entry) => entry.kind === "computer" || entry.kind === "tool"))),
  );
}

/** Keep only live app/computer work in the automatic tray card. */
export function isComputerWorkToastPinned(item: WorkToastItem | undefined): boolean {
  return Boolean(item && !item.dismissed && item.state === "working" && hasComputerUseEvidence(item));
}

/** A working run that is driving the desktop itself, not API-only app work. */
export function isDrivingComputer(item: WorkToastItem | undefined): boolean {
  return isComputerWorkToastPinned(item) && Boolean(item?.computer);
}

/** The same execution under either key: its standalone card or its task card. */
function sameRun(a: WorkToastItem, b: WorkToastItem): boolean {
  return workToastExecutionId(a) === workToastExecutionId(b);
}

function needsUser(item: WorkToastItem): boolean {
  return !item.dismissed && ["question", "approval", "blocked", "failed"].includes(item.state);
}

/** Whether a later toast of the same conversation takes the mini tray over
 *  from `current`. A run that is still driving the computer keeps its card,
 *  and its Stop, against another run: it only gives way to itself under its
 *  task key and to something that needs the user. */
export function yieldsToConversationToast(current: WorkToastItem | undefined, incoming: WorkToastItem | undefined): boolean {
  if (!current?.conversationId || !incoming || current.key === incoming.key) return false;
  if (current.conversationId !== incoming.conversationId) return false;
  return !isDrivingComputer(current) || sameRun(current, incoming) || needsUser(incoming);
}

export function keepPinnedComputerWorkToast(current: WorkToastItem | undefined, incoming: WorkToastItem | undefined): boolean {
  if (!current || !isComputerWorkToastPinned(current)) return false;
  if (incoming && (sameRun(current, incoming) || needsUser(incoming))) return false;
  return !yieldsToConversationToast(current, incoming);
}
